import { EventPeriod, Line, MarketGroup } from '@/constants';
import { ILiveFeedDataAPI } from '@/types/feed';
import { Ref, ref } from 'vue';
import { listSiteDataTooltip } from '@/helpers/feed';
import { AbstractFeed } from '@/models/abstractFeed';
import { BetItem } from '@/models/bet';

export class FeedLiveItem extends AbstractFeed {
    //Stats
    public time: Ref<string | null>;
    public timeList: [number, string, number, number][] | null;
    public isTimeMismatch: Ref<0 | 1>;
    public redHome: Ref<number | null>;
    public redAway: Ref<number | null>;
    public yellowHome: Ref<number | null>;
    public yellowAway: Ref<number | null>;
    public scoreHome: Ref<number | null>;
    public scoreAway: Ref<number | null>;
    public scoreList: Ref<[number, string][] | null>;
    public isScoreMismatch: Ref<0 | 1>;
    public situation: Ref<string | null>;
    public possessionTimeHome: Ref<number | null>;
    public possessionTimeAway: Ref<number | null>;
    public attacksHome: Ref<number | null>;
    public attacksAway: Ref<number | null>;
    public dangerousAttacksHome: Ref<number | null>;
    public dangerousAttacksAway: Ref<number | null>;
    public shotsHome: Ref<number | null>;
    public shotsAway: Ref<number | null>;
    public shotsOnTargetHome: Ref<number | null>;
    public shotsOnTargetAway: Ref<number | null>;
    public hitWoodworkHome: Ref<number | null>;
    public hitWoodworkAway: Ref<number | null>;
    public foulsHome: Ref<number | null>;
    public foulsAway: Ref<number | null>;
    public cornersHome: Ref<number | null>;
    public cornersAway: Ref<number | null>;

    private previousScoreHome: number | null;
    private previousScoreAway: number | null;
    private lastChangedScoreHome: number | null = null;
    private lastChangedScoreAway: number | null = null;
    private lastGoalAt: number = 0;

    public position: Ref<string | null> = ref(null);

    constructor(periodTypeId: EventPeriod, data: ILiveFeedDataAPI) {
        super(periodTypeId, data);

        this.time = ref(data.time ?? null);
        this.timeList = data.atime ?? null;
        this.isTimeMismatch = ref(data.tDiff!);
        if (data.sc) {
            const [home, away] = data.sc.split('-');
            this.scoreHome = ref(Number(home));
            this.scoreAway = ref(Number(away));
            this.previousScoreHome = this.scoreHome.value;
            this.previousScoreAway = this.scoreAway.value;
        } else {
            this.scoreHome = ref(null);
            this.scoreAway = ref(null);
            this.previousScoreHome = null;
            this.previousScoreAway = null;
        }
        this.scoreList = ref(data.asc ?? null);
        this.isScoreMismatch = ref(data.scDiff!);
        this.situation = ref(data.si ?? null);
        this.redHome = ref(data.rHome ?? null);
        this.redAway = ref(data.rAway ?? null);
        this.yellowHome = ref(data.yHome ?? null);
        this.yellowAway = ref(data.yAway ?? null);
        this.possessionTimeHome = ref(data.pHome ?? null);
        this.possessionTimeAway = ref(data.pAway ?? null);
        this.attacksHome = ref(data.aHome ?? null);
        this.attacksAway = ref(data.aAway ?? null);
        this.dangerousAttacksHome = ref(data.daHome ?? null);
        this.dangerousAttacksAway = ref(data.daAway ?? null);
        this.shotsHome = ref(data.sHome ?? null);
        this.shotsAway = ref(data.sAway ?? null);
        this.shotsOnTargetHome = ref(data.stHome ?? null);
        this.shotsOnTargetAway = ref(data.stAway ?? null);
        this.hitWoodworkHome = ref(data.hwwHome ?? null);
        this.hitWoodworkAway = ref(data.hwwAway ?? null);
        this.foulsHome = ref(data.fHome ?? null);
        this.foulsAway = ref(data.fAway ?? null);
        this.cornersHome = ref(data.cHome ?? null);
        this.cornersAway = ref(data.cAway ?? null);

        if (data.line === Line.Main && (this.scoreHome.value !== null || this.cornersHome.value !== null)) {
            this.calculatePosition();
        }
    }

    public update(data: Partial<ILiveFeedDataAPI>): void {
        super.update(data);
        if (data.time !== undefined) this.time.value = data.time;
        if (data.atime !== undefined) this.timeList = data.atime;
        if (data.tDiff !== undefined) this.isTimeMismatch.value = data.tDiff;
        if (data.sc !== undefined) {
            if (data.sc === null) {
                this.scoreHome.value = this.scoreAway.value = null;
            } else {
                const [homeStr, awayStr] = data.sc.split('-');
                const home = Number(homeStr);
                const away = Number(awayStr);

                if ((this.previousScoreHome !== null && home !== this.previousScoreHome) || (this.previousScoreAway !== null && away !== this.previousScoreAway)) {
                    this.lastChangedScoreHome = this.previousScoreHome;
                    this.lastChangedScoreAway = this.previousScoreAway;
                    this.lastGoalAt = Date.now();
                    console.log('Goal at ' + this.lastGoalAt);
                }
                this.previousScoreHome = this.scoreHome.value;
                this.previousScoreAway = this.scoreAway.value;
                this.scoreHome.value = home;
                this.scoreAway.value = away;
            }
        }
        if (data.asc !== undefined) this.scoreList.value = data.asc;
        if (data.scDiff !== undefined) this.isScoreMismatch.value = data.scDiff;
        if (data.si !== undefined) this.situation.value = data.si;
        if (data.rHome !== undefined) this.redHome.value = data.rHome;
        if (data.rAway !== undefined) this.redAway.value = data.rAway;
        if (data.yHome !== undefined) this.yellowHome.value = data.yHome;
        if (data.yAway !== undefined) this.yellowAway.value = data.yAway;
        if (data.pHome !== undefined) this.possessionTimeHome.value = data.pHome;
        if (data.pAway !== undefined) this.possessionTimeAway.value = data.pAway;
        if (data.aHome !== undefined) this.attacksHome.value = data.aHome;
        if (data.aAway !== undefined) this.attacksAway.value = data.aAway;
        if (data.daHome !== undefined) this.dangerousAttacksHome.value = data.daHome;
        if (data.daAway !== undefined) this.dangerousAttacksAway.value = data.daAway;
        if (data.sHome !== undefined) this.shotsHome.value = data.sHome;
        if (data.sAway !== undefined) this.shotsAway.value = data.sAway;
        if (data.stHome !== undefined) this.shotsOnTargetHome.value = data.stHome;
        if (data.stAway !== undefined) this.shotsOnTargetAway.value = data.stAway;
        if (data.fHome !== undefined) this.foulsHome.value = data.fHome;
        if (data.fAway !== undefined) this.foulsAway.value = data.fAway;
        if (data.cHome !== undefined) this.cornersHome.value = data.cHome;
        if (data.cAway !== undefined) this.cornersAway.value = data.cAway;
        if (data.hwwHome !== undefined) this.hitWoodworkHome.value = data.hwwHome;
        if (data.hwwAway !== undefined) this.hitWoodworkAway.value = data.hwwAway;

        if (this.line.value === Line.Main && (data.sc !== undefined || data.cHome !== undefined || data.cAway !== undefined)) {
            this.calculatePosition();
        }
    }

    public get timeTooltip(): string {
        if (this.scoreHome.value !== null && this.time.value === null) {
            return this.t('feed.unavailableTimeTooltip');
        } else if (this.scoreHome.value === null && this.time.value === null) {
            return this.t('feed.unavailableTooltip');
        }

        return listSiteDataTooltip(this.timeListLocalisation(this.timeList!), this.leagueId.value, this.limitsCategoryId.value);
    }

    public get displayedTime(): string {
        // console.log(`displayedTime execute for event ${this.teamHomeName.value}`);
        if (this.time.value === null) {
            return this.t('feed.unavailable');
        }
        return this.timeLocalisation(this.time.value);
    }

    public get scoreTooltip(): string {
        if (this.scoreHome.value !== null) {
            return listSiteDataTooltip(this.scoreList.value!, this.leagueId.value, this.limitsCategoryId.value);
        } else {
            return this.t('feed.unavailableTooltip');
        }
    }

    public get scoreFull(): string {
        if (this.scoreHome.value === null) {
            return '';
        }
        return `${this.scoreHome.value}-${this.scoreAway.value}`;
    }

    public get cornersSum(): number {
        if (this.cornersHome.value === null) {
            return 0;
        }
        return Number(this.cornersHome.value) + Number(this.cornersAway.value);
    }

    public get displayedScore(): string {
        // console.log(`displayedScore execute for event ${this.teamHomeName.value}`);
        if (this.scoreHome.value === null) {
            return '? - ?';
        }

        let diffTime = (Date.now() - this.lastGoalAt) / 1000; //difference time in sec between current score and lastChangedScore

        let scoreClass = '';
        let scoreHomeClass = '';
        let scoreAwayClass = '';
        if (diffTime < 20) {
            //console.log('Score changed, yellow');
            scoreClass = 'highlightYellow';
            if (this.lastChangedScoreHome !== this.scoreHome.value) {
                scoreHomeClass = ' redColorScore';
            } else {
                scoreHomeClass = ' blackColorScore';
            }

            if (this.lastChangedScoreAway !== this.scoreAway.value) {
                scoreAwayClass = ' redColorScore';
            } else {
                scoreAwayClass = ' blackColorScore';
            }
        }

        if (this.isScoreMismatch.value) {
            const homeScores = this.scoreList.value!.map((entry) => entry[1]?.split('-')[0]);
            const awayScores = this.scoreList.value!.map((entry) => entry[1]?.split('-')[1]);
            const firstHomeScore = homeScores[0];
            const firstAwayScore = awayScores[0];
            const isHomeScoresDiff = homeScores.some((score) => score !== firstHomeScore);
            const isAwayScoresDiff = awayScores.some((score) => score !== firstAwayScore);
            if (isHomeScoresDiff) {
                scoreHomeClass += ' redText';
            }
            if (isAwayScoresDiff) {
                scoreAwayClass += ' redText';
            }
        }

        return `<span class="${scoreClass}">
                <span class="${scoreHomeClass}">${this.scoreHome.value}</span> - <span class="${scoreAwayClass}">${this.scoreAway.value}</span>
                </span>`.trim();
    }

    public get eventSituationTooltip(): string {
        if (!this.situation.value) {
            return '';
        }

        if (this.situation.value.includes('U')) {
            return this.t('feed.underdogLeadingBy') + ' ' + this.situation.value.substring(1);
        } else if (this.situation.value.includes('F')) {
            return this.t('feed.favouriteLeadingBy') + ' ' + this.situation.value.substring(1);
        } else if (this.situation.value === 'T') {
            return this.t('feed.tie');
        }

        return '';
    }

    public get displayedEventSituation(): string {
        if (!this.situation.value) {
            return '';
        }

        if (this.situation.value.includes('U')) {
            return this.t('feed.underdogLeadingByAbbr') + this.situation.value.substring(1);
        } else if (this.situation.value.includes('F')) {
            return this.t('feed.favouriteLeadingByAbbr') + this.situation.value.substring(1);
        } else if (this.situation.value === 'T') {
            return this.t('feed.tieAbbr');
        }

        return '';
    }

    public getEventScoreForBet = (bet: BetItem): { home: number | null; away: number | null } => {
        if (bet.marketGroup === MarketGroup.Corners) {
            if ((this.time.value !== null && this.time.value.includes('1H')) || bet.isFT()) {
                return { home: this.cornersHome.value, away: this.cornersAway.value };
            } else {
                //1h bet and (2nd half or unknown time)
                return { home: bet.cornersHomeIrHt, away: bet.cornersAwayIrHt };
            }
        } else {
            if ((this.time.value !== null && this.time.value.includes('1H')) || bet.isFT()) {
                return { home: this.scoreHome.value, away: this.scoreAway.value };
            } else {
                //1h bet and (2nd half or unknown time)
                return { home: bet.scoreHomeIrHt, away: bet.scoreAwayIrHt };
            }
        }
    };

    protected timeLocalisation(time: string): string {
        if (this.locale === 'en') {
            return time;
        }

        // "1H" -> "1T", "2H" -> "2T", "FT" -> "FIM", "HT" -> "INT"
        let localized = time;
        localized = localized
            .replace(/\b1H\b/g, '1T')
            .replace(/\b2H\b/g, '2T')
            .replace(/\bFT\b/g, 'FIM')
            .replace(/\bHT\b/g, 'INT');
        return localized;
    }

    protected timeListLocalisation(timeList: [number, string, number, number][]): [number, string, number, number][] {
        if (this.locale === 'en') {
            return timeList;
        }

        return timeList.map(([bookmakerId, time, n2, n3]) => [
            bookmakerId,
            time
                .replace(/\b1H\b/g, '1T')
                .replace(/\b2H\b/g, '2T')
                .replace(/\bFT\b/g, 'FIM')
                .replace(/\bHT\b/g, 'INT'),
            n2,
            n3,
        ]);
    }
}
